import { DateHeaderCellProps, Scheduler, SchedulerItem, SchedulerItemContent, SchedulerItemProps, SchedulerViewSlot, WeekView } from "@progress/kendo-react-scheduler";
import TimeCell from "components/Calendar/TimeCell";
import Translation from "components/Translation";
import { useAppContext } from "context/AppContext";
import { AuthContext, useAuth } from "context/AuthContext";
import { useTenantContext } from "context/TenantContext";
import { useEffect, useState } from "react";
import { CalendarSlotItem, StartDay, ViewMode } from "types/calendar";
import { LocationItem } from "types/location";
import { createModal } from "../ModalFactory";
import { ModalWrapper } from "../ModalWrapper";
import QRCode from 'qrcode.react'
import { classNames } from "utils/dom-helpers";
import { toHour } from "utils/string-localization-helper";
import getConfig from "next/config";

const ModalProvider = createModal(
    AuthContext,
    'CalendarPrintModal',
    () => <></>,
    () => <></>
)



export default function CalendarPrintModal() {
    const { CalendarPrintModal } = useAuth()
    const { attributes, is_open, close } = CalendarPrintModal
    const { primaryColor, tenant, default_booking_url, calendar_settings } = useTenantContext()
    const { locale } = useAppContext()
    const [is_safari, toggleSafari] = useState<boolean>(false)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    function DateCell(p: DateHeaderCellProps) {
        return (
            <div
                {...p}
                className={classNames(
                    p.className || '',
                    'border-l-white px-0.5 border-r-none'
                )}
            >
                <div className="text-left w-full">
                    <div className="block font-thin border-b border-black text-xs py-1.5">
                        {p.date.toLocaleDateString('default', {
                            weekday: 'long',
                        })}
                    </div>
                    {attributes?.hide_month_day
                        ?
                        <div className="h-[37px]" />
                        :
                        <strong className="text-[26px] mt-2">
                            {p.date.toLocaleDateString('default', {
                                day: '2-digit',
                            })}
                        </strong>
                    }

                </div>
            </div>
        )
    }

    function SlotItem(props: SchedulerItemProps) {
        let offset: number = 0
        //@ts-ignore, chrome check
        let is_chrome = !!window.chrome
        let is_mac = window.navigator.appVersion.indexOf("Mac") != -1
        let day = (props.dataItem.start as Date).getDay()
        offset = calendar_settings.start_day == StartDay.SUNDAY
            ? day
            : (day + 7 - 1) % 7
        if (is_chrome && is_mac) {
            offset = ((offset * (12 / window.devicePixelRatio)) - (offset * 2))       
        } else {
            offset = ((offset * 4.5) - (offset * 2))
        }

        return (
            <SchedulerItem
                {...props}
                style={{
                    backgroundColor: props.dataItem.primary_hex || "",
                    marginLeft: offset > 0 ? `${window.devicePixelRatio >= 1 ? "-" : ""}${offset}px` : "",
                }}
            >
                <SchedulerItemContent>
                    <div
                        title={props.dataItem?.service_name || ''}
                        className="flex flex-col truncate text-ellipsis z-10 leading-tight cursor-pointer text-[10px] p-0.5"
                    >
                        <div className="flex flex-wrap justify-between mb-0.5 font-bold">
                            <span
                                className="inline-block truncate"
                                title={props.dataItem?.service_name}
                            >
                                {props.dataItem?.service_name}
                            </span>
                        </div>
                        <span
                            className="inline-block truncate"
                        >
                            {toHour(props.dataItem?.start)} - {toHour(props.dataItem?.end)}
                        </span>
                        <div
                            className="inline-block truncate items-center"
                            title={props.dataItem?.staff_name}
                        >
                            <i className="feather-user mr-0.5" />
                            {props.dataItem?.staff_name}
                        </div>
                    </div>
                </SchedulerItemContent>
            </SchedulerItem>
        )
    }

    useEffect(() => {
        if (is_open) {
            setTimeout(function () {
                window.print()
                close()
            }, 300)
        }
    }, [is_open])

    useEffect(() => {
        //@ts-ignore, chrome check cuz it has safari too for some reason
        toggleSafari(!window.chrome && navigator.userAgent.includes("Safari"))
    }, [])

    return (
        <ModalProvider.Visible>
            <ModalWrapper
                rootClassName="w-screen h-screen bg-white overflow-hidden"
                className={`flex w-screen h-screen items-center content-center overflow-hidden bg-white`}
                zIndex="z-[100]"
            >
                <div
                    id="calendar-print"
                    className="flex w-fit h-fit max-h-fit overflow-visible z-[100] bg-white"
                    onClick={close}
                    style={{
                        position: "static", //using static fixes the color in safari for some reason, DO NOT CHANGE
                    }}
                >
                    <div
                        className='flex flex-row w-full h-full'
                        style={{
                            printColorAdjust: "exact"
                        }}
                    >
                        <div className='flex flex-col py-4 w-fit text-white relative shrink-0'
                            style={{
                                backgroundColor: primaryColor
                            }}
                        >
                            <div className="border-l-2 border-white px-3">
                                <div className="font-display text-2xl font-[450]">
                                    {tenant && tenant.name}
                                </div>
                                {attributes?.location &&
                                    <div className="block mt-4 text-sm font-[450]">
                                        {(attributes.location as LocationItem).name}
                                    </div>
                                }
                            </div>
                            <div className="items-center text-center w-fit mt-2.5">
                                <QRCode
                                    value={default_booking_url}
                                    className="w-[95px] h-[95px] mx-6 p-1 bg-white"
                                />
                            </div>
                            <Translation
                                content_key='calendar.weekly_schedule'
                                render_as='div'
                                className='font-[450] text-[50px] rotate-180 mt-auto leading-[100px] z-10 whitespace-nowrap'
                                style={{
                                    writingMode: 'vertical-rl'
                                }}
                            />
                            <img
                                src='https://static.aot.plus/images/crescent_vector_large.svg'
                                className='absolute bottom-0 left-0'
                            />
                        </div>
                        <div className='flex flex-col w-full h-full overflow-visible bg-white p-1.5'>
                            <div className='flex flex-row justify-between mb-3'>
                                <div className='flex flex-col ml-6'>
                                    <div className='text-xs text-[#857F7F] font-[450] leading-[15px] my-auto'>
                                        <Translation content_key='calendar.as_of' />
                                        &nbsp;
                                        {new Date().toLocaleDateString()}
                                    </div>
                                    <div hidden={attributes?.hide_month_day as boolean} className='text-2xl text-neutral-900 font-bold leading-[30px]'>
                                        {(attributes?.date as Date)?.toLocaleString(locale, { month: "long" })}
                                    </div>
                                </div>
                                <img src={tenant?.bizLogoUrl || "https://static.aot.plus/images/aot_logo.svg"} className="w-[44px] h-[44px]" />
                            </div>
                            < Scheduler
                                data={(attributes?.items as CalendarSlotItem[])}
                                view={ViewMode.WEEK}
                                header={() => <></>}
                                footer={() => <></>}
                                className="border-0 max-h-fit w-[57%] print:w-full"
                                height={"fit-content max-h-fit"}
                                date={(attributes?.date as Date)}
                                editable={false}
                                viewSlot={(props) => {
                                    return (
                                        <span
                                            className={classNames('w-full h-full border overflow-hidden text-[10px] bg-white',
                                                is_safari ? "box-content max-h-[72px]" : ""
                                            )}
                                        >
                                            <SchedulerViewSlot
                                                {...props}
                                                className={classNames(props.className || '', 'g-view-slot h-[28px] min-h-[28px]')}
                                            />
                                        </span>
                                    )
                                }}
                                item={SlotItem}
                            >
                                <WeekView
                                    slotDivisions={2}
                                    slotDuration={60}
                                    dateHeaderCell={DateCell}
                                    timeHeaderCell={TimeCell}
                                    workDayStart={attributes?.day_start as string}
                                    workDayEnd={attributes?.day_end as string}
                                    className="calendar-widget"
                                    currentTimeMarker={false}
                                />
                            </Scheduler>
                            <Translation
                                content_key='calendar.class_schedule_subject_to_change'
                                render_as='div'
                                className='w-full justify-center leading-[12px] text-[10px] font-[450] text-[#857F7F] text-center mt-6'
                            />
                        </div>
                    </div>
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}